'use client'

import { classNames } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { getMsgDeskRuntime } from '@/msgdeskRuntime'
import { treeStore } from '@/msgdeskRuntime/state'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import { SmartNameKey } from './SmartNameKey'
import { ExtendedNameKey } from './ExtendedNameKey'
import { useEditorSection, useEditorFormModelSnapshot } from '../../EditorTemplateContract'
import { useEditorSurface } from '../../EditorSurfaceContext'
import { scopedNameKey, MAX_NAMEKEY_LENGTH } from '@/contracts/namekeys'
import { NameScopeControl } from '@/app/[msg]/components/NameScopeControl'

function editorField(name: string): HTMLInputElement | HTMLTextAreaElement | null {
  const root = getMsgDeskRuntime().querySelector('[data-md-contract="editor.root"]')
  return root?.querySelector<HTMLInputElement | HTMLTextAreaElement>(`[name="${name}"]`) || null
}

function assertNameKeyContracts(isExt: boolean) {
  const setVisible = treeStore.mutate.setContractVisible
  if (typeof setVisible !== 'function') return
  if (treeStore.mutate.hasContract?.('editor.smartName.root')) setVisible('editor.smartName.root', !isExt)
  if (treeStore.mutate.hasContract?.('editor.extName.root')) setVisible('editor.extName.root', isExt)
}

// Std namekey object: ONE compound with an exclusive compact/extended switch,
// matching legacy toggle_head_smart semantics — the mode fact (el_rough
// .name_key.is_ext) is legacy-meaningful (auto-generate targets and the saver
// read it), so it lives in the rough via set-section. The VISUAL swap is
// React-owned; both blocks stay mounted so their inputs remain in the form.
// On switch the visible value follows the user (legacy copies it too); the
// check verdict deliberately resets — check/save re-validate.
export function NameKeyBlock() {
  const { variant, skin } = useEditorSurface()
  const senderSection = useEditorSection('senderOptions')
  const advancedOpen = senderSection.state.open ?? false
  const model = useEditorFormModelSnapshot()
  const scope = model?.nameScope || ''
  const localName = (advancedOpen ? model?.fields.nameKey : model?.fields.smartNameKey) || ''
  const fullAddress = scopedNameKey(scope, localName)
  const localMaxLength = Math.max(1, MAX_NAMEKEY_LENGTH - (scope ? scope.length + 1 : 0))
  const scopeChoice = model?.isMsg && model.nameScopeParent ? (
    <NameScopeControl scope={scope} parent={model.nameScopeParent} address={fullAddress}
      hasLocalName={!!localName} isUpdate={model.isUpdate || Number(model.draftElId) > 0}
      onChange={enabled => dispatchEditorAction({ action: 'set-name-scope', enabled })} />
  ) : null

  if (variant === 'mailbox') {
    return (
      <div className={classNames('md-name-key', skin.nameKey.block)}>
        {scopeChoice}
        <SmartNameKey maxLength={localMaxLength} nameScope={scope} />
        <button
          type="button"
          className={skin.nameKey.moreButton}
          onClick={() => senderSection.update({ open: !advancedOpen }, 'mailbox:sender-options')}
          aria-expanded={advancedOpen}
        >
          {advancedOpen ? 'Hide sender options' : 'More sender options'}
        </button>
        <div className={advancedOpen ? skin.nameKey.advancedOpen : skin.nameKey.advancedClosed}>
          <ExtendedNameKey maxLength={localMaxLength} nameScope={scope} />
        </div>
      </div>
    )
  }

  const isExt = advancedOpen

  const switchMode = (nextExt: boolean) => {
    if (nextExt === isExt) return
    const smartInput = editorField('name_key_smart')
    const extInput = editorField('name_key')
    const value = (nextExt ? smartInput?.value : extInput?.value) ?? ''

    if (nextExt) {
      if (extInput) extInput.value = value
      dispatchEditorAction({ action: 'set-extended-name-key', value })
    } else {
      if (smartInput) smartInput.value = value
      dispatchEditorAction({ action: 'set-smart-name-key', value })
    }

    senderSection.update({ open: nextExt }, 'std:name-key-mode')
    assertNameKeyContracts(nextExt)
    getMsgDeskRuntime().setTimeout(() => {
      ;(nextExt ? extInput : smartInput)?.focus()
    }, 0)
  }

  return (
    <div className={classNames('md-name-key', skin.nameKey.block)} data-md-name-key-mode={isExt ? 'extended' : 'compact'}>
      {scopeChoice}
      <div className={classNames('md-name-key__compact', isExt ? skin.nameKey.advancedClosed : skin.nameKey.advancedOpen)}>
        <SmartNameKey maxLength={localMaxLength} nameScope={scope} />
      </div>
      <div className={classNames('md-name-key__extended', isExt ? skin.nameKey.advancedOpen : skin.nameKey.advancedClosed)}>
        <ExtendedNameKey maxLength={localMaxLength} nameScope={scope} />
      </div>
      <div className={classNames('md-name-key__switch-row', skin.nameKey.switchRow)}>
        <button
          type="button"
          className={classNames('md-name-key__switch', skin.nameKey.switchButton)}
          onClick={() => switchMode(!isExt)}
          aria-expanded={isExt}
        >
          {isExt ? '↑ Simple name' : '↓ More name options'}
        </button>
      </div>
    </div>
  )
}
